import { useEffect, useState } from "react";
import { motion, AnimatePresence, useScroll, useMotionValueEvent } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NavLink, useNavigate } from "react-router-dom";
import { useLang } from "@/contexts/LanguageContext";
import { useBookNow } from "@/hooks/useBookNow";

// Import your local logo image
// Option 1: If logo is in src/assets/images/
import logoImage from "@/assets/logo.jpg";

// Option 2: If logo is in a different folder
// import logoImage from "@/assets/logo_6.jpg";
// import logoImage from "@/assets/images/logo/logo_6.jpg";

// Option 3: If logo is in public folder, use a string path
// const logoImage = "/images/logo_6.jpg";
// const logoImage = "/assets/images/logo_6.jpg";

const links = [
  { to: "/track", key: "track" },
  { to: "/fleet", key: "fleet" },
  { to: "/pricing", key: "pricing" },
  { to: "/experience", key: "experience" },
  { to: "/gallery", key: "gallery" },
  { to: "/events", key: "events" },
  { to: "/advertise", key: "advertise" },
  { to: "/faq", key: "faq" },
  { to: "/contact", key: "contact" },
];

export const Navigation = () => {
  const { lang, toggle, t } = useLang();
  const navigate = useNavigate();
  const bookNow = useBookNow();
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [open, setOpen] = useState(false);
  const { scrollY, scrollYProgress } = useScroll();

  useMotionValueEvent(scrollY, "change", (latest) => {
    const prev = scrollY.getPrevious() ?? 0;
    setScrolled(latest > 20);
    // Hide on scroll-down past 200px, reveal on scroll-up
    setHidden(latest > 200 && latest > prev);
  });

  // Close mobile menu on route change
  useEffect(() => {
    const close = () => setOpen(false);
    return close;
  }, []);

  // Lock body scroll while the mobile menu is open so the page behind
  // doesn't scroll under the overlay.
  useEffect(() => {
    if (!open) return undefined;
    const scrollY = window.scrollY;
    const { body } = document;
    const prev = {
      position: body.style.position,
      top: body.style.top,
      width: body.style.width,
      overflow: body.style.overflow,
    };
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.width = "100%";
    body.style.overflow = "hidden";
    return () => {
      body.style.position = prev.position;
      body.style.top = prev.top;
      body.style.width = prev.width;
      body.style.overflow = prev.overflow;
      window.scrollTo(0, scrollY);
    };
  }, [open]);

  const go = (to) => {
    navigate(to);
    setOpen(false);
    window.scrollTo({ top: 0, behavior: "instant" });
  };

  return (
    <>
      <motion.header
        initial={{ y: -30, opacity: 0 }}
        animate={{
          y: hidden ? -100 : 0,
          opacity: 1,
        }}
        transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed top-9 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled || open ? "glass-nav" : ""
        }`}
        data-testid="main-nav"
      >
        {/* Top accent line â€” animates in when scrolled */}
        <motion.div
          aria-hidden="true"
          initial={{ scaleX: 0 }}
          animate={{ scaleX: scrolled ? 1 : 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#FF2D2D] to-transparent origin-center"
        />

        <motion.div
          animate={{
            height: scrolled ? 56 : 80,
          }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-screen-2xl mx-auto px-6 md:px-10 flex items-center justify-between"
        >
          <motion.button
            onClick={() => go("/")}
            className="flex items-center gap-3 group"
            data-testid="logo-btn"
            animate={{ scale: scrolled ? 0.88 : 1 }}
            transition={{ duration: 0.3 }}
          >
            <img
              src={logoImage}
              alt="Karting Track Sofia"
              className="h-9 md:h-11 w-auto"
              style={{ mixBlendMode: "screen" }}
            />
          </motion.button>

          <nav className="hidden md:flex items-center gap-7">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                onClick={() => window.scrollTo({ top: 0, behavior: "instant" })}
                className={({ isActive }) =>
                  `relative text-xs font-medium tracking-[0.18em] uppercase transition-colors group ${
                    isActive ? "text-[#FF2D2D]" : "text-white/70 hover:text-[#FF2D2D]"
                  }`
                }
                data-testid={`nav-${l.key}-btn`}
              >
                {({ isActive }) => (
                  <>
                    {t.nav[l.key]}
                    <span
                      className={`absolute -bottom-1 left-0 h-px bg-[#FF2D2D] transition-all duration-300 ${
                        isActive ? "w-full" : "w-0 group-hover:w-full"
                      }`}
                    />
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3 md:gap-4">
            <button
              onClick={toggle}
              className="font-mono-display text-xs tracking-widest border border-white/20 hover:border-[#FF2D2D] hover:text-[#FF2D2D] px-3 py-1.5 transition-colors"
              data-testid="lang-toggle-btn"
            >
              {lang === "en" ? "EN / BG" : "BG / EN"}
            </button>
            <button
              onClick={bookNow}
              className="hidden sm:inline-flex items-center gap-2 bg-[#FF2D2D] text-white px-5 py-2.5 text-xs font-bold tracking-[0.16em] uppercase hover:bg-white hover:text-black transition-colors group"
              data-testid="nav-book-btn"
            >
              <span className="block w-1.5 h-1.5 rounded-full bg-white group-hover:bg-[#FF2D2D] animate-pulse" />
              {t.nav.book}
            </button>
            <button
              className="md:hidden text-white p-1 -mr-1"
              onClick={() => setOpen((p) => !p)}
              data-testid="mobile-menu-btn"
              aria-label="Menu"
            >
              {open ? <X size={26} /> : <Menu size={26} />}
            </button>
          </div>
        </motion.div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 bg-black/95 backdrop-blur-xl md:hidden flex flex-col overflow-y-auto"
            data-testid="mobile-menu-panel"
          >
            <div className="min-h-full flex flex-col items-center justify-center gap-4 sm:gap-5 px-6 pt-24 pb-10">
              {links.map((l, i) => (
                <motion.button
                  key={l.to}
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.04 * i }}
                  onClick={() => go(l.to)}
                  className="font-display text-2xl sm:text-3xl font-bold uppercase tracking-tight text-white hover:text-[#FF2D2D] transition-colors"
                  data-testid={`mobile-nav-${l.key}-btn`}
                >
                  {t.nav[l.key]}
                </motion.button>
              ))}
              <button
                onClick={() => { setOpen(false); bookNow(); }}
                className="mt-4 bg-[#FF2D2D] text-white px-8 py-3 text-sm font-bold tracking-[0.16em] uppercase"
                data-testid="mobile-book-btn"
              >
                {t.nav.book}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navigation;
